---
title: Deploy the app
summary: Run the app with Docker Compose locally, or deploy to Azure with the provided scripts.
visibility: authenticated
audience: operator
section: Operating
order: 120
last_verified: 2026-06-19
owner: docs
---

# Deploy the app

The app ships as Docker images — a frontend (nginx) and a backend (FastAPI) — with PostgreSQL
in production. The same image runs every feature profile; configuration is runtime-only.

## Docker Compose (local / self-hosted)

**Production-style:** create a `.env` at the project root with at least a `SECRET_KEY`
(generate one with `python -c "import secrets; print(secrets.token_urlsafe(32))"`), then:

```bash
docker compose up -d
```

This starts PostgreSQL, the backend, and the frontend (nginx on port 3000).

**Development (hot reload):**

```bash
docker compose -f docker-compose.dev.yml up
```

## Build/run helpers

The `bin/` scripts detect your platform and pick the right image build:

```bash
bin/build-auto.sh    # build images for this platform
bin/up-auto.sh       # start the stack
bin/dev.sh --with-backend   # frontend + backend dev servers, no Docker
```

Always verify changes via Docker before deploying — containers are the source of truth.

## Azure

Azure deployments use a single combined image and the scripts under `bin/azure/`:

- `setup.sh` — provision/configure an environment (also sets the RAG routing base).
- `deploy.sh` — build and push a new image to the target environment.
- `status.sh` — check a deployment's current state.

Each environment has a `config.<name>.sh` file holding its settings (subscription, resource
group, profile, RAG instances). Pin the right config when running the scripts.

## First run

On first startup the backend seeds an administrator account and **forces a password change on
first login**. The default credentials and other startup values come from the environment —
see [Configuration](/help/operator/configuration).

## See also

- [Configuration](/help/operator/configuration) — environment variables and profiles.
- [RAG routing](/help/operator/rag-routing) — point the app at your RAG server(s).
- [Health and troubleshooting](/help/operator/health-and-troubleshooting).
